import { useState, useEffect, useRef, useCallback } from 'react';

import ErrorBoundary from '../../../app/layout/ErrorBoundary';
import Platform from '../../../app/layout/header/Platform';
import { useAppContext } from '../../../app/provider/useAppContext';
import { useEventContext } from '../../../app/provider/useEventContext';
import { appConfig } from '../../../config/app-config';
import Loader from '../../../shared/components/Loader';
import { CLICK_OUTSIDE_EVENT } from '../../../shared/constants/event.constants';
import { formatDateRange as formatDateRangeUtil } from '../../../shared/utils/dateUtil';
import { DELEGATE_RATE_CONFIRMATION_TEXT } from '../../cart/constants/cartConstants';
import DiscountBanner from '../../discount/components/DiscountBanner';
import DiscountInfo from '../../discount/components/DiscountInfo';
import GroupDiscount from '../../discount/components/GroupDiscount';
import type { DiscountBlock } from '../../discount/types/discount.types';
import PassConfigurationModal from '../../product/components/PassConfigurationModal';
import ProductPassList from '../../product/components/ProductPassList';
import type { ProductWithAddOns } from '../../product/types/product.types';
import { useBasketAction } from '../hooks/useBasketAction';
import { useHomeProducts, getAttribute } from '../hooks/useHomeProducts';
import type { CustomerGroup } from '../types/home.types';


import PromoBanner from './banner/PromoBanner';
import ContentBlock from './content-block/ContentBlock';
import RateCardList from './rate-selector/RateCardList';


function HomePage() {
  const { eventData: globalEventData, customerGroups, loading: globalLoading, error: eventError } = useEventContext();
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [rateConfirmed, setRateConfirmed] = useState(false);
  const [selectedPass, setSelectedPass] = useState<ProductWithAddOns | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  useAppContext();

  const rateCardListRef = useRef<HTMLDivElement>(null);
  const passListRef = useRef<HTMLDivElement>(null);

  // -------------------------------------------------------------------------
  // Hooks — data fetching & basket action
  // -------------------------------------------------------------------------
  const { products, productsLoading, productsError, refetch: fetchProducts } = useHomeProducts();

  const { handleAddToBasket } = useBasketAction({
    selectedPass,
    selectedRateId,
    customerGroups: (customerGroups ?? []) as CustomerGroup[],
    quantity,
    onSuccess: () => {
      setIsModalOpen(false);
      setSelectedPass(null);
      setSelectedAddOns({});
      setSelectedOptions([]);
      setQuantity(1);
    },
  });

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideRateList = rateCardListRef.current?.contains(target) ?? false;
      const insidePassList = passListRef.current?.contains(target) ?? false;

      if (insideRateList || insidePassList || isModalOpen) {
        return;
      }

      const isPortalClick =
        (target as HTMLElement).closest?.('.Toastify') ||
        (target as HTMLElement).closest?.('[role="dialog"]');
      if (isPortalClick) return;
      setSelectedPass(null);
      setSelectedAddOns({});
      setSelectedOptions([]);
      setQuantity(1);
    };
    document.addEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
  }, [isModalOpen]);

  useEffect(() => {
    if (!globalLoading && customerGroups && customerGroups.length > 0 && !selectedRateId) {
      const availableRates = (customerGroups as CustomerGroup[]).filter((group) => {
        const fields = group.custom?.fields as Record<string, unknown> | undefined;
        if (fields?.delegateRateToggle !== 'enable') return false;
        const now = new Date();
        const validFrom = fields?.validFrom ? new Date(fields.validFrom as string) : null;
        const validUntil = fields?.validUntil ? new Date(fields.validUntil as string) : null;
        if (validFrom && now < validFrom) return false;
        if (validUntil && now > validUntil) return false;
        return true;
      });

      if (availableRates.length > 0) {
        setSelectedRateId(availableRates[0].id);
      }
    }
  }, [globalLoading, customerGroups, selectedRateId]);

  // -------------------------------------------------------------------------
  // UI helpers
  // -------------------------------------------------------------------------
  const formatDateRange = (product: ProductWithAddOns | null): string => {
    if (!product) return '';
    const startDate = getAttribute(product, 'startDate') as string | null;
    const endDate = getAttribute(product, 'endDate') as string | null;
    return formatDateRangeUtil(startDate, endDate) || '';
  };

  const getRawOptions = (): unknown[] => {
    if (!selectedPass) return [];
    const attributes = selectedPass.masterVariant?.attributes ?? [];
    const eventSessionsAttr = attributes.find((a) => a.name === 'options');
    return (eventSessionsAttr?.value as unknown[]) ?? [];
  };

  const getIsMultiOption = (): boolean => {
    if (!selectedPass) return false;
    const attributes = selectedPass.masterVariant?.attributes ?? [];
    const isMultiOptionAttr = attributes.find((a) => a.name === 'allowMultiOptions');
    return (isMultiOptionAttr?.value as boolean) ?? false;
  };

  const getGroupDiscountData = (): Record<string, unknown> => {
    return (globalEventData?.groupDiscount as Record<string, unknown>) ?? {};
  };

  const getDiscountBlocksData = (): DiscountBlock[] => {
    return (globalEventData?.discountBlocks as DiscountBlock[]) ?? [];
  };

  const selectedRateRequiresConfirmation = (): boolean => {
    if (!selectedRateId || !customerGroups) return false;
    const selectedGroup = (customerGroups as CustomerGroup[]).find((g) => g.id === selectedRateId);
    const ctField = (selectedGroup?.custom?.fields as Record<string, unknown>)?.['rateConfirmation'];
    if (ctField) return true;
    const groupName =
      typeof selectedGroup?.name === 'object'
        ? (selectedGroup.name as Record<string, string>)[appConfig.defaults.locale] ?? ''
        : (selectedGroup?.name as string) ?? '';
    return Boolean((DELEGATE_RATE_CONFIRMATION_TEXT as Record<string, string>)[groupName]);
  };

  const handleRateSelect = useCallback((rateId: string) => {
    setSelectedRateId((prev) => {
      if (prev === rateId) return prev;
      setRateConfirmed(false);
      return rateId;
    });
  }, []);

  const isPassEnabled = selectedRateId !== null && (!selectedRateRequiresConfirmation() || rateConfirmed);

  const getSelectedPassPrice = () => {
    const prices = selectedPass?.masterVariant?.prices;
    if (!prices || prices.length === 0) return null;
    if (!selectedRateId || !prices.some((p) => p.customerGroup)) return prices[0];
    let matched = prices.find((p) => p.customerGroup?.id === selectedRateId);
    if (!matched && Array.isArray(customerGroups)) {
      const selectedGroup = (customerGroups as CustomerGroup[]).find((g) => g.id === selectedRateId);
      if (selectedGroup?.key) {
        matched = prices.find((p) => p.customerGroup?.obj?.key === selectedGroup.key);
      }
    }
    return matched ?? prices[0];
  };

  const getSelectedDelegateRateToggles = (): { showAddOns: boolean; showOptions: boolean } => {
    if (!selectedRateId || !customerGroups) {
      return { showAddOns: true, showOptions: true };
    }
    const selectedGroup = (customerGroups as CustomerGroup[]).find((g) => g.id === selectedRateId);
    if (!selectedGroup) {
      return { showAddOns: true, showOptions: true };
    }
    const fields = (selectedGroup.custom?.fields ?? {}) as Record<string, string>;
    return {
      showAddOns: fields.addonsToggle === 'enable',
      showOptions: fields.optionsToggle === 'enable',
    };
  };

  const handlePassSelection = (pass: ProductWithAddOns) => {
    setSelectedPass(pass);
    setQuantity(1);
    setSelectedAddOns({});
    setSelectedOptions([]);

    const attributes = pass.masterVariant?.attributes ?? [];
    const eventSessionsAttr = attributes.find((a) => a.name === 'options');
    const isMultiOption = (attributes.find((a) => a.name === 'allowMultiOptions')?.value as boolean) ?? false;

    if (!isMultiOption && eventSessionsAttr?.value && Array.isArray(eventSessionsAttr.value) && eventSessionsAttr.value.length > 0) {
      setSelectedOptions(['session-0']);
    }

    setIsModalOpen(true);
  };

  const handleAddOnSelection = (addOnId: string, isSelected: boolean) => {
    setSelectedAddOns((prev) => ({ ...prev, [addOnId]: isSelected }));
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (globalLoading || productsLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[calc(100vh-160px)]">
        <Loader text="Loading..." fullScreen={true} />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[calc(100vh-160px)] px-4">
        <p className="text-red-600 mb-4 text-center">
          {eventError instanceof Error ? eventError.message : 'Failed to load event configuration'}
        </p>
        <a
          href={window.location.pathname}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
        >
          Refresh page
        </a>
      </div>
    );
  }

  return (
    <>
      <Platform eventData={globalEventData} loading={globalLoading} />
      {globalEventData?.promoBanner && (
        <PromoBanner text={globalEventData.promoBanner.description} />
      )}
      <DiscountBanner discountData={globalEventData?.discountBanner} />
      <div id="delegate-pass-selection" className="w-full flex flex-col items-center bg-[#F9FAFB] scroll-mt-20">
        <div className="w-full max-w-[1240px] mx-auto flex flex-col lg:flex-row gap-[24px] pt-[16px] pb-[72px] px-[20px] md:px-[40px]">

          <div className="w-full lg:w-[40%] flex flex-col">
            <h2 className="font-semibold text-[16px] text-[#203a6b] mb-[12px] md:mb-[32px] text-left">
              1. Choose your delegate type
            </h2>
            <div ref={rateCardListRef}>
              <RateCardList
                rates={(customerGroups ?? [])
                  .filter((group: CustomerGroup) => {
                    const fields = group.custom?.fields as Record<string, unknown> | undefined;
                    if (fields?.delegateRateToggle !== 'enable') return false;
                    const now = new Date();
                    const validFrom = fields?.validFrom ? new Date(fields.validFrom as string) : null;
                    const validUntil = fields?.validUntil ? new Date(fields.validUntil as string) : null;
                    if (validFrom && now < validFrom) return false;
                    if (validUntil && now > validUntil) return false;
                    return true;
                  })
                  .map((group: CustomerGroup) => {
                    const fields = (group.custom?.fields as Record<string, unknown>) ?? {};
                    const desc = (fields['description'] as string) ?? '';
                    const groupName =
                      typeof group.name === 'object'
                        ? (group.name as Record<string, string>)[appConfig.defaults.locale] ?? ''
                        : (group.name as string) ?? '';
                    const rateConfirmation =
                      (fields['rateConfirmation'] as string) ||
                      (DELEGATE_RATE_CONFIRMATION_TEXT as Record<string, string>)[groupName] ||
                      undefined;
                    return {
                      id: group.id,
                      title: groupName,
                      description: desc,
                      rateConfirmation,
                    };
                  })}
                selectedRateId={selectedRateId}
                onRateSelect={handleRateSelect}
                onConfirmedChange={(rateId, confirmed) => {
                  if (rateId === selectedRateId) setRateConfirmed(confirmed);
                }}
              />
            </div>

            <div className="mt-[32px] hidden lg:flex flex-col gap-[16px]">
              <>
                {getGroupDiscountData().groupDiscountTitle && (
                  <GroupDiscount data={getGroupDiscountData()} />
                )}

                {getDiscountBlocksData().length > 0 && (
                  <DiscountInfo discountBlocks={getDiscountBlocksData()} />
                )}
              </>
            </div>
          </div>

          <div className="w-full lg:w-[60%] flex flex-col gap-8">
            <div className="flex flex-col">
              <h2 className="font-semibold text-[16px] text-[#203a6b] mb-[12px] md:mb-[32px] text-left">
                2. Choose your pass
              </h2>
              {productsError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 mb-2">{productsError.message}</p>
                  <button
                    type="button"
                    onClick={() => fetchProducts()}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium text-sm"
                  >
                    Retry
                  </button>
                </div>
              )}
              <ErrorBoundary
                fallback={(err) => (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 mb-2">{err.message || 'Failed to load products'}</p>
                    <button
                      type="button"
                      onClick={() => fetchProducts()}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium text-sm"
                    >
                      Retry
                    </button>
                  </div>
                )}
              >
                <div ref={passListRef}>
                  <ProductPassList
                    products={products}
                    selectedPassId={selectedPass?.id}
                    onPassSelect={handlePassSelection}
                    selectedRateId={selectedRateId}
                    customerGroups={customerGroups as CustomerGroup[]}
                    isEnabled={isPassEnabled}
                  />
                </div>
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>

      <PassConfigurationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          selectedPass
            ? (typeof selectedPass.name === 'object'
                ? (selectedPass.name as Record<string, string>)[appConfig.defaults.locale]
                : selectedPass.name) ?? ''
            : ''
        }
        description={formatDateRange(selectedPass)}
        price={(() => {
          const p = getSelectedPassPrice();
          return p?.discounted?.value?.centAmount ?? p?.value?.centAmount ?? 0;
        })()}
        originalPrice={(() => {
          const p = getSelectedPassPrice();
          return p?.discounted ? (p.value?.centAmount ?? null) : null;
        })()}
        currency={getSelectedPassPrice()?.value?.currencyCode ?? 'USD'}
        rawOptions={getRawOptions()}
        rawAddOns={selectedPass?.addOns ?? []}
        activeCurrency={sessionStorage.getItem('currency') || 'USD'}
        isMultiOption={getIsMultiOption()}
        selectedOptions={selectedOptions}
        onOptionChange={setSelectedOptions}
        selectedAddOns={selectedAddOns}
        onSelectionChange={handleAddOnSelection}
        onAddToBasket={handleAddToBasket}
        buttonText="Add to basket"
        quantity={quantity}
        onQuantityChange={setQuantity}
        showAddOns={getSelectedDelegateRateToggles().showAddOns}
        showOptions={getSelectedDelegateRateToggles().showOptions}
      />

      <ContentBlock
        features={globalEventData?.contentBlocks ?? []}
        buttonLabel={globalEventData?.contentBlockButtonLabel}
      />
    </>
  );
}

export default HomePage;
